import { DEV } from 'cc/env';
import { CCFloat, Component, Label, random, _decorator } from 'cc';
import RollingNumber from './RollingNumber';

const { ccclass, property } = _decorator;

enum EventType {
    RESET_POSITION = 'RESET_POSITION',
}

@ccclass('RollingNumberUnit')
export default class RollingNumberUnit extends Component {
    public static readonly EVENT_TYPE = EventType;

    @property({ type: CCFloat })
    public numberHeight: number = 0;
    @property([Label])
    private readonly labels: Label[] = [];
    @property({ tooltip: DEV && '上往下滾動, false時則為下往上' })
    private readonly isRollingUpToDown: boolean = true;

    public digitIndex: number = 0;
    // TODO: 要搭配 SlowMode?
    private _currentDigitOffset: number = 0;
    private _isCarrying: boolean = false;
    private _rollingNumber: RollingNumber = null;
    private _nextUnit: RollingNumberUnit = null;
    private _previousUnit: RollingNumberUnit = null;
    private _isBaseUnitRolling: boolean = false;
    private _usingRandomNumber: boolean = false;
    // private _isCarryByCheckingStatus = false;

    public get direct() {
        return this.isRollingUpToDown ? -1 : 1;
    }

    public get isPreparingCarrying() {
        return this.isAddValue ? this.currentValue === 9 : this.currentValue === 0;
    }

    public get isAddValue() {
        return this.currentSpeed > 0;
    }

    public get isMoveUpToDown() {
        return this.direct * (this.isAddValue ? 1 : -1) < 0;
    }
    public get isCarrying() {
        return this._isCarrying;
    }

    // 基準之上的 unit 都當作 gear 被帶動
    public get isGearMode() {
        return this.currentDigitOffset + +this._rollingNumber.isSlowMode > 0;
    }

    public get isBaseUnit() {
        return this.currentDigitOffset + +this._rollingNumber.isSlowMode === 0;
    }

    public get isFastMode() {
        return this.currentDigitOffset + +this._rollingNumber.isSlowMode < 0;
    }

    public get currentSpeed() {
        const speedList = this._rollingNumber.currentSpeedList;
        if (this.currentDigitOffset >= 0) {
            return speedList[0];
        }
        return speedList[Math.abs(this.currentDigitOffset) - +this._rollingNumber.isSlowMode];
    }

    public get currentDigitOffset() {
        return this._currentDigitOffset;
    }

    public set currentDigitOffset(value: number) {
        this._currentDigitOffset = value;
        if (this._currentDigitOffset === 0) {
            this._isBaseUnitRolling = true;
            this.clearFollowUpCarrying();
        } else {
            this._isBaseUnitRolling = false;
        }
    }

    public get currentValue() {
        return +this.labels[1].string;
    }

    public set currentValue(value: number) {
        if (this.isGearMode) {
            if (this._previousUnit) {
                this._previousUnit._isCarrying = false;
            }

            if (this.digitIndex === this._rollingNumber.unitLength - 1) {
                this._isCarrying = false;
            }
        }
        if (value === 1 && !this.node.active) {
            this.node.active = true;
        }
        // 若 Base 或 Gear 是 9 (倒轉時是 1)，則需要準備 carry 下一個單位
        const isCarry = this.isAddValue ? value === 9 : value === 0;
        if ((this.isBaseUnit || this.isGearMode) && isCarry) {
            this.carryNextUnit();
        }
        if (this._isBaseUnitRolling && !this.isBaseUnit) {
            this._isBaseUnitRolling = false;
        }
        this._setLabel(value);
        this._resetPosition();
    }

    protected get labelHeight() {
        return this.numberHeight || this.labels[0].node.transformHeight;
    }

    public init(rollingNumber: RollingNumber, digitIndex: number, nextUnit: RollingNumberUnit, previousUnit: RollingNumberUnit) {
        this._rollingNumber = rollingNumber;
        this.digitIndex = digitIndex;
        this._nextUnit = nextUnit;
        this._previousUnit = previousUnit;
        // 確保一開始上下隱藏數字也是正確的
        this._setLabel(+this.labels[1].string);
        this.updateFont();
    }

    public updateRollingProgress(deltaTime: number) {
        if (this.isGearMode) {
            this.updateGear(deltaTime);
            return;
        }
        if (this._usingRandomNumber) {
            this._setRandomNumber();
        }
        this._moveUnit(deltaTime, this.currentSpeed);
    }

    public updateGear(deltaTime: number) {
        // 確保上一個單位已經被帶動、自身需要被帶動、確認目標值是需要被帶動
        if (this._previousUnit?._isCarrying || this._isBaseUnitRolling) {
            // Gear 用上一個單位的速度確保轉動速度一樣
            this._moveUnit(deltaTime, this._previousUnit.currentSpeed);

            // 用於檢查 RollingNumber 在 speed 變更時可能多位移一次
            // if (!this._isBaseUnitRolling && !this._previousUnit.isPreparingCarrying) {
            //     if (this._previousUnit._isCarryByCheckingStatus) {
            //         warn(`Gear Rolling By CheckingStatus`);
            //         this._previousUnit._isCarryByCheckingStatus = false;
            //     }
            //     log(
            //         `Gear Rolling Error: Type = ${this._rollingNumber.node.name},Index = ${this.digitIndex}, Current Number = ${this.currentValue}, Previous Unit Number is ${this._previousUnit.currentValue}`,
            //     );
            // }
        }
    }

    // 導正原本 FastMode 亂跑的數值
    public setFastModeUnitValue(lastTargetValue: number, nextDigitOffset: number) {
        const isKeepFastMode = nextDigitOffset + +this._rollingNumber.isSlowMode < 0;
        if (!this.isFastMode || isKeepFastMode) {
            return;
        }

        // 避免原本亂跑的數值轉到一半被切換成正常數字導致原本的數字卡在一半
        if (nextDigitOffset - this.currentDigitOffset > 1) {
            lastTargetValue -= 1;
            if (lastTargetValue < 0) {
                lastTargetValue += 10;
            }
            if (this._previousUnit) {
                this._previousUnit._isCarrying = true;
            }
        }

        // 原本 FastMode 時有 Carry，要確認更改後的數字是需要 Carry 下一位的
        const isCarry = this.isAddValue ? lastTargetValue === 9 : lastTargetValue === 0;
        if (!isCarry && this._isCarrying) {
            this._isCarrying = false;
            // error(`${this._rollingNumber.node.name} setFastModeUnitValue wrong carry status`);
        }

        this._setLabel(lastTargetValue);
    }

    public skipRollingOnDecrease(value: number) {
        this._resetPosition();
        this._setLabel(value);
        this.updateFont();
    }

    // 用來更新 Fast Mode 的字體
    public updateFont(forceRandomNumber: boolean = false) {
        this._usingRandomNumber = forceRandomNumber && this.isFastMode;
        if (!this._rollingNumber.normalFont) {
            return;
        }

        const font =
            this._rollingNumber.fastModeFont &&
            !this.isGearMode &&
            (Math.abs(this.currentSpeed) > this._rollingNumber.blurThreshold || (forceRandomNumber && !this.isBaseUnit))
                ? this._rollingNumber.fastModeFont
                : this._rollingNumber.normalFont;
        this.labels.forEach((label) => {
            label.font = font;
        });
    }

    // BaseUnit call 該 function 即可
    // 確認 Base Unit 及之後的 Unit 是否有需要 Carry
    public checkCarryStatus() {
        if (this.isPreparingCarrying) {
            // this._isCarryByCheckingStatus = true;
            this.carryNextUnit();
        }
    }

    public carryNextUnit() {
        // 用基準 Unit 帶動下一個單位
        if (this.isBaseUnit) {
            this.setCarrying();
        }
    }

    // 在倒轉的時候用，只需第一個 Unit 呼叫該 function
    // 清除 Carry 後只幫 Base Unit 設定 Carry
    public resetCarrying() {
        this._isCarrying = false;
        this._isBaseUnitRolling = false;
        // 確認倒轉時是否有還沒跑完的 Unit ，並確認 Carry 狀態、轉速是否需要更新
        if (!this.isFastMode && this.currentSpeed !== 0) {
            if ((this.isRollingUpToDown && this.node.y < -this.labelHeight / 2) || (!this.isRollingUpToDown && this.node.y > this.labelHeight / 2)) {
                this._setLabel(this.currentValue + 1);
                this._resetPosition();
            }
            if (this._previousUnit?.isBaseUnit && this._previousUnit?.isPreparingCarrying) {
                this._previousUnit.setCarrying(false);
            }
        }
        this._nextUnit?.resetCarrying();
    }

    protected setCarrying(needToSetNextUnit = true) {
        if (this.currentSpeed === 0) {
            return;
        }
        this._isCarrying = true;
        if (needToSetNextUnit) {
            if (this._nextUnit?.isPreparingCarrying) {
                this._nextUnit.setCarrying();
            }
        }
    }

    protected clearFollowUpCarrying() {
        if (this._nextUnit) {
            this._nextUnit._isCarrying = false;
            this._nextUnit.clearFollowUpCarrying();
        }
    }

    // 處理 unit 移動，超過邊界要更改值
    private _moveUnit(deltaTime: number, speed: number) {
        this.node.y += this.labelHeight * (deltaTime * speed * this.direct);
        if (this.isMoveUpToDown ? this.node.y < -this.labelHeight : this.node.y > this.labelHeight) {
            this.node.y += this.labelHeight * -this.direct * 2;
            this.currentValue += this.isAddValue ? 1 : -1;
        }
    }

    private _setLabel(value: number) {
        value = Math.floor(value + 10);
        if (this.isRollingUpToDown) {
            this.labels[0].string = `${(value + 1) % 10}`;
            this.labels[1].string = `${value % 10}`;
            this.labels[2].string = `${(value - 1) % 10}`;
        } else {
            this.labels[2].string = `${(value + 1) % 10}`;
            this.labels[1].string = `${value % 10}`;
            this.labels[0].string = `${(value - 1) % 10}`;
        }
    }

    private _setRandomNumber() {
        this.labels[2].string = `${Math.floor(random() * 10)}`;
        this.labels[1].string = `${Math.floor(random() * 10)}`;
        this.labels[0].string = `${Math.floor(random() * 10)}`;
    }

    private _resetPosition() {
        this.node.y = 0;
        this.emit(RollingNumberUnit.EVENT_TYPE.RESET_POSITION);
    }
}
